import Base2DElement from "./base-2d-element";
import RenderManager from "../rendering/render-manager";
import Transition from "./Transition";
import SlideManager from "../utils/slide-manager";
import distToSegmentSquared from "../utils/distanceToSegment";
import roundPathCorners from "../utils/rounding";
import * as Events from "../utils/events";
import EntranceExit from "./EntranceExit";

const VERTEX_SIZE = 15;
function mapVectors(data) {
    const out = [];

    for (let i = 0, len = data.length; i < len; i += 2) {
        out.push(new THREE.Vector2().fromArray(data, i));
    }

    return out;
}
class CustomizableRegion extends Base2DElement {

    get type2d() { return "CustomizableRegion"; }

    constructor(options, parentLayer, uuid) {
        super(options, parentLayer, uuid);

        parentLayer.domElement.setAttribute("contains-region", "");

        this._path = document.createElementNS(Base2DElement.Namespace, "path");
        this._path_editor = document.createElementNS(Base2DElement.Namespace, "path");
        this.domElement.appendChild(this._path);
        if (options.className) {
            this.domElement.classList.add(options.className)
        }
        this._lastMousePosition = new THREE.Vector3();
        this._lastMouseOverPoint = null;
        this._mouseOverVisualizer = Q3.editMode ? document.createElementNS(Base2DElement.Namespace, "circle") : null;
        this._mouseDown = false;

        this._selectedIndexPoint = -1;
        this._selectedIndexLine = -1;
        this._hasClicked = false;
        this._editing = false;
        this._rounded = false;
        this._roundRadius = 0;
        if(!this._arcs) {
            this._arcs = [];
        }

        if(!this._bezier) {
            this._bezier = [];
        }

        this._inLetterbox = options.inLetterbox !== false;

        this._path.setAttribute("pointer-events", "auto");

        if (Q3.editMode) {
            this._mouseOverVisualizer.setAttribute("r", VERTEX_SIZE / RenderManager.pixelRatio);
            this._mouseOverVisualizer.setAttribute("display", "none");
            this.domElement.appendChild(this._mouseOverVisualizer);

            this._path_editor.setAttribute("fill", "rgba(13, 11, 28, 0.1)");
            this._path_editor.setAttribute("stroke", "rgba(0,0,0,0)");
        }
        this.setDefaultStyles();

        const actions = this.data.action;

        if (actions && actions.length && !Q3.editMode) {
            actions.forEach(action => {
                this._path.classList.add("clickable");
                const transition = new Transition(action.systemAction, action.value);
                this._path.addEventListener("click", e => {
                    transition.click();
                })
            });
        }

        let lastClickTime = 0;

        this._path.addEventListener("mousedown", e => { this._mouseDown = true; });
        this._path.addEventListener("mouseup", e => { this._mouseDown = false; });

        this._path.addEventListener("mouseover", e => {
            if (!this.data.disableEvents) this.setHoverStyles()
        });
        this._path.addEventListener("mouseleave", e => {
            if (!this.data.disableEvents) this.setDefaultStyles();
        });

        this._path.addEventListener("click", e => {

            this._recalculateBounds();

            this.selectHandler(e)

        }, false);

        this.redraw = this.redraw.bind(this);

        this.redraw();
        this.onResize = this.onResize.bind(this);

        if (Q3.isReady) {
            this.handleListeners(true);
            this.needsUpdate = true;
        } else {
            Q3.Events.listen("tourReady", () => {
                this.handleListeners(true);
                this.needsUpdate = true;
            });
        }
        if (Q3.editMode) {
            this.clonesFo = document.createElementNS(Base2DElement.Namespace, "foreignObject");
            this.clonesFo.style.overflow = "visible";
            this.clonesDiv = document.createElement("div");
            this.clonesDiv.className = "clones-container"
            this.clonesFo.appendChild(this.clonesDiv);
            this.parentLayer.domElement.appendChild(this.clonesFo);
        }
        this._recalculateBounds();
    }

    createRemoveEl(onClick) {
        this.removeFromQuizonClick = this.removeFromQuizonClick || onClick;
        let removeFromQuizFo = document.createElementNS(Base2DElement.Namespace, "foreignObject");
        let removeFromQuiz = document.createElement("div");
        removeFromQuizFo.addEventListener("click", this.removeFromQuizonClick);
        removeFromQuiz.className = 'remove-from-quiz';
        removeFromQuiz.style.display = this.domElement.classList.contains("selected-2d") ? "block" : "none";
        removeFromQuizFo.appendChild(removeFromQuiz);
        this.domElement.appendChild(removeFromQuizFo);

        const pr = RenderManager.pixelRatio;
        const {width: _width, height: _height} = RenderManager.getSize();
        const width = _width / pr;
        const height = _height / pr;
        const { fnx, fny } = this._getFunctions(width, height);
        removeFromQuizFo.setAttribute("width", "20px");
        removeFromQuizFo.setAttribute("height", "20px");
        let arr = this.points.map(p => Math.sqrt(p.y*p.y + (1-p.x)*(1-p.x)));
        let p = this.points[arr.indexOf(Math.min(...arr))];
        removeFromQuizFo.setAttribute("x", fnx(p.x)-10);
        removeFromQuizFo.setAttribute("y", fny(p.y)-10);
        this.removeFromQuiz = this.removeFromQuiz || new Map();
        this.removeFromQuiz.set(this.domElement, removeFromQuiz);
    }

    destroyRemoveEl() {
        this.removeFromQuiz.get(this.domElement).remove()
    }

    handleListeners(add) {
      Events[add ? "listen" : "unlisten"]("viewportchanged", this.onResize);
    }

    onResize() {
        let size = this.getVWSize();
        let points = this._points;
        let arcs = this._arcs;
        let bezier = this._bezier;
        if (this.data[size] && this.data[size].points) {
            points = mapVectors(this.data[size].points)
        }
        if (this.data[size] && this.data[size].arcs) {
            arcs = this.data[size].arcs
        }
        if (this.data[size] && this.data[size].bezier) {
            bezier = this.data[size].bezier;
        }
        this._points = points;
        this._arcs = arcs;
        this._bezier = bezier;
    }

    setDefaultStyles() {
        this._path.setAttribute("fill", this.backgroundColor);
        this._path.setAttribute("stroke", this.borderColor);
        this._path.setAttribute("stroke-width", this.borderWidth);
    }
    setHoverStyles() {
        this._path.setAttribute("stroke", this.hoverBorderColor);
        this._path.setAttribute("fill", this.hoverBackgroundColor);
        this._path.setAttribute("stroke-width", this.hoverBorderWidth);
    }
    setData(data) {
        const calcOffset = () => {
            const curTopLeft = new THREE.Vector2().copy(this._xy);
            const curBottomRight = new THREE.Vector2().copy(curTopLeft).add(this._wh);
            const curCenter = new THREE.Vector2().copy(curBottomRight).add(curTopLeft).multiplyScalar(0.5);

            const dstTopLeft = new THREE.Vector2(data.x, data.y);
            const dstBottomRight = new THREE.Vector2(data.width, data.height).add(dstTopLeft);
            const dstCenter = new THREE.Vector2().copy(dstBottomRight).add(dstTopLeft).multiplyScalar(0.5);

            return { offset: new THREE.Vector2().subVectors(dstCenter, curCenter), center: dstCenter };
        }

        const calcExpansion = () => {
            const curScale = new THREE.Vector2().copy(this._wh);
            const dstScale = new THREE.Vector2(data.width < 0.01 ? 0.01 : data.width, data.height < 0.01 ? 0.01 : data.height);

            const scale = new THREE.Vector2().copy(dstScale).divide(curScale);

            return scale;

        };

        const { offset, center } = calcOffset();
        const scale = calcExpansion();

        this._points.forEach(p => p.add(offset).sub(center).multiply(scale).add(center));

        this.needsUpdate = true;
        this._recalculateBounds();

        this._updateQ3Data();
    }

    get points() {
        return this._points;
    }

    set points(data) {
        this._points = mapVectors(data);
        const { width, height } = RenderManager.getSize();
        this.redraw(width, height);
    }

    get arcs() {
        return this._arcs;
    }

    set arcs(data) {
        this._arcs = data;
    }

    get bezier() {
        return this._bezier;
    }

    set bezier(data) {
        this._bezier = data;
    }

    getRatio() {
        const { width, height } = RenderManager.getSize();
        return width / height;
    }

    get editing() { return this._editing; }
    set editing(v) {
        if (!Q3.editMode) return;

        this._editing = v;
        if (v) {
            Q3.container.classList.add("editing-region");
            this._path.setAttribute("pointer-events", "none");
        } else {
            this._path.setAttribute("pointer-events", "auto");
            Q3.container.classList.remove("editing-region");
        }

        this.needsUpdate = true;
    }

    set rounded(radius) {
        this._rounded = radius > 0;
        this._roundRadius = radius;
        const { width, height } = RenderManager.getSize();
        this.redraw(width, height);
    }

    delete() {
      this.handleListeners(false);
    }

    _updateMouseUpEditing(position) {
        if (this._selectedIndexPoint === -1 && this._selectedIndexLine === -1 && !this._hasClicked) {
            this.editing = false;
            this.needsUpdate = false;
            return;
        }

        this._selectedIndexLine = -1;
        this._selectedIndexPoint = -1;

        if (Q3.selected === this) this._updateQ3Data();
    }

    onMouseUp(event, position) {
        position = position.clone().multiplyScalar(RenderManager.pixelRatio);

        if (this._editing) {
            this._updateMouseUpEditing(position);
            Events.fire("2d_changed");
        }
        this._mouseDown = false;
    }

  _updateQ3Data() {
    let size = this.getVWSize();
    if (!this.data[size]) {
      this.data[size] = {};
    }
    this.data.points = this.data[size].points = this._points.map(p => p.toArray()).flat();
    this.data.arcs = this.data[size].arcs = this._arcs;
    this.data.bezier = this.data[size].bezier = this._bezier;
  }
    _updateMouseDownEditing(position) {
        if (this._selectedIndexLine >= 0 && event.which === 1) {
            const { width, height } = RenderManager.getSize();
            const { ifnx, ifny } = this._getFunctions(width, height);

            const v = new THREE.Vector2(ifnx(this._lastMousePosition.x), ifny(this._lastMousePosition.y));

            this._points.splice(this._selectedIndexLine + 1, 0, v);
            this._selectedIndexPoint = this._selectedIndexLine + 1;
            this._selectedIndexLine = -1;
            this.needsUpdate = true;
            this._mouseOverVisualizer.setAttribute("display", "none");
        } else if (this._selectedIndexPoint >= 0 && event.which === 3 && this._points.length > 3) {
            this._points.splice(this._selectedIndexPoint, 1);
            this._selectedIndexPoint = -1;
            this.needsUpdate = true;
        }
    }

    onMouseDown(event, position) {
        position = position.clone().multiplyScalar(RenderManager.pixelRatio);
        if (this._editing) {
            Events.fire("2d_change_start", [{ object: this, properties: ["points"] }]);
            this._updateMouseDownEditing(position);
        }
        this._mouseDown = true;
    }

    _recalculateBounds() {
        let minX = Infinity, minY = Infinity;
        let maxX = -Infinity, maxY = -Infinity;

        this._points.forEach(p => {
            const { x, y } = p;

            minX = Math.min(minX, x);
            minY = Math.min(minY, y);

            maxX = Math.max(maxX, x);
            maxY = Math.max(maxY, y);
        });

        this._xy.set(minX, minY);
        this._wh.set(maxX, maxY).sub(this._xy);
    }

    _updateMouseMoveEditing(position) {
        if (!this._mouseDown) {
            if (this._lastMouseOverPoint) {
                this._lastMouseOverPoint.setAttribute("fill", "black");
                this._lastMouseOverPoint = null;
            }

            this._selectedIndexLine = -1;
            this._selectedIndexPoint = -1;

            if (this._mouseOverVisualizer) {
                this._mouseOverVisualizer.setAttribute("display", "none");
            }

            const { width, height } = RenderManager.getSize();
            const { fnx, fny } = this._getFunctions(width, height);

            const dstSqr = Math.pow(VERTEX_SIZE * 2 / RenderManager.pixelRatio, 2);

            const closestIndex = this._points.reduce((closest, p, i) => {

                const x = fnx(p.x), y = fny(p.y);
                const d = Math.pow(x - position.x, 2) + Math.pow(y - position.y, 2);

                if (closest.d > d && d < dstSqr) {
                    closest.d = d;
                    closest.index = i;
                }

                return closest;
            }, { d: Infinity, index: -1 }).index;

            if (closestIndex >= 0) {
                this._selectedIndexPoint = closestIndex;
                const closest = this.domElement.querySelector(`[index="${closestIndex}"]`);

                this._lastMouseOverPoint = closest;

                closest.setAttribute("fill", "red");
            } else {
                const lines = this._points.slice();
                lines.push(this._points[0]);

                const v1 = new THREE.Vector2(), v2 = new THREE.Vector2();
                let closestDistance = Infinity, closestIndex = -1, closestProjection = null;

                for (let i = 1, len = lines.length; i < len; i++) {
                    const a = lines[i - 1], b = lines[i];

                    v1.set(fnx(a.x), fny(a.y));
                    v2.set(fnx(b.x), fny(b.y));

                    const { projected, distance } = distToSegmentSquared(position, v1, v2);

                    const dstSqr = Math.pow(VERTEX_SIZE * 2 / RenderManager.pixelRatio, 2);

                    if (closestDistance > distance && distance < dstSqr) {
                        closestIndex = i - 1;
                        closestDistance = distance;
                        closestProjection = projected;
                    }
                }

                if (closestIndex >= 0) {
                    const pr = RenderManager.pixelRatio;

                    position = closestProjection;
                    this._selectedIndexLine = closestIndex;
                    this._mouseOverVisualizer.removeAttribute("display");
                    this._mouseOverVisualizer.setAttribute("cx", closestProjection.x / pr);
                    this._mouseOverVisualizer.setAttribute("cy", closestProjection.y / pr);
                }
            }
        } else {
            if (this._selectedIndexPoint >= 0) {
                const { width, height } = RenderManager.getSize();
                const pr = RenderManager.pixelRatio;
                const { ifnx, ifny } = this._getFunctions(width, height);

                const point = this.domElement.querySelector(`[index="${this._selectedIndexPoint}"]`);
                this._lastMouseOverPoint = point;

                point.setAttribute("fill", "red");
                point.setAttribute("cx", position.x / pr);
                point.setAttribute("cy", position.y / pr);

                this._points[this._selectedIndexPoint].set(ifnx(position.x), ifny(position.y));
                this.needsUpdate = true;
            }
        }
    }


    onMouseMove(event, position) {
        position = position.clone().multiplyScalar(RenderManager.pixelRatio);

        if (this.needsUpdate || !this._editing) return;

        if (this._editing) this._updateMouseMoveEditing(position);

        this._lastMousePosition.copy(position);
    }

    _createContainer() { return document.createElementNS(Base2DElement.Namespace, "g"); }

    _initDefaultVars() {
        super._initDefaultVars();

        this._points = [
            new THREE.Vector2(0, 0),
            new THREE.Vector2(0.25, 0),
            new THREE.Vector2(0.25, 0.25),
            new THREE.Vector2(0, 0.25),
        ];
    }

    _getFunctions(width, height) {
        const pr = RenderManager.pixelRatio;

        if (!Q3.editMode && this.inLetterbox) {
            width = Q3.letterbox.offsetWidth / RenderManager.pixelRatio;
            height = (Q3.letterbox.offsetHeight) / RenderManager.pixelRatio;
        }

        const wpr = width * pr;
        const hpr = height * pr;

        const fnx = x => x * wpr, ifnx = x => x / wpr;
        const fny = y => y * hpr, ifny = y => y / hpr;

        return { fnx, fny, ifnx, ifny };
    }

    _createPathString(fnx, fny, join) {
        if (this._points.length <= 1) return "";

        return `M ${fnx(this._points[0].x)} ${fny(this._points[0].y)} ` +
            this._points.slice(1).map(p => `L ${fnx(p.x)} ${fny(p.y)}`).join(" ") +
            (join ? `L ${fnx(this._points[0].x)} ${fny(this._points[0].y)}` : "");
    }

    _createCurvedPathString(fnx, fny, join) {
        if (this._points.length <= 1) return "";

        let path = [];

        path.push(`M ${fnx(this._points[0].x)} ${fny(this._points[0].y)} `);
        for(let i = 1; i < this._points.length; ++i) {
            const p = this._points[i];
            path.push(`L ${fnx(p.x)} ${fny(p.y)} `);
        }
        if(join) path.push(`L ${fnx(this._points[0].x)} ${fny(this._points[0].y)}`);

        let pathString = "";

        for(let i = 0; i < path.length; ++i) {
            let arc = this._arcs.find( item => item.pos === i);
            if(arc) {
                const r = fnx(arc.r);
                const large = arc.large !== undefined ? arc.large : 0;
                const sweep = arc.sweep !== undefined ? arc.sweep : 1;
                const insertAt = i < this._points.length ? i : 0;
                pathString += `A ${r} ${r} 0 ${large} ${sweep} ${fnx(this._points[insertAt].x)} ${fny(this._points[insertAt].y)} `;
            }

            if(this._bezier.includes(i) && this._points[i]) {
                const endPoint = i < this._points.length - 1 ? i + 1 : 0;
                pathString += `Q ${fnx(this._points[i].x)} ${fny(this._points[i].y)} ${fnx(this._points[endPoint].x)} ${fny(this._points[endPoint].y)} `;
            }
            else {
                pathString += path[i] + " ";
            }
        }

        return pathString;
    }

    _initValues(options) {
        this.backgroundColor = "backgroundColor" in options ? options.backgroundColor : "rgba(13, 11, 28, 0.1)";
        this.borderColor = "borderColor" in options ? options.borderColor : "rgba(0,0,0,0)";
        this.borderWidth = "borderWidth" in options ? options.borderWidth : "0";
        this.hoverBackgroundColor = "hoverBackgroundColor" in options ? options.hoverBackgroundColor : "rgba(13, 11, 28, 0.2)";
        this.hoverBorderColor = "hoverBorderColor" in options ? options.hoverBorderColor : "rgba(0,0,0,0)";
        this.hoverBorderWidth = "hoverBorderWidth" in options ? options.hoverBorderWidth : "0";
        let size = this.getVWSize();
        let pointsData = options[size] && options[size].points ? options[size].points : "points" in options ? options.points : false;
        this._points = pointsData ? mapVectors(pointsData) : this._points;
        let arcsData = options[size] && options[size].arcs ? options[size].arcs : "arcs" in options ? options.arcs : false;
        this._arcs = arcsData ? arcsData : this._arcs;
        let bezierData = options[size] && options[size].bezier ? options[size].bezier : "bezier" in options ? options.bezier : false;
        this._bezier = bezierData ? bezierData : this._bezier;
    }

    redraw(width, height) {
        const pr = RenderManager.pixelRatio;

        if (!width || !height) {
            const { width: _width, height: _height } = RenderManager.getSize();

            if (!width) width = _width / pr;
            if (!height) height = _height / pr;
        }

        if (!isFinite(width) || !isFinite(height)) return;
        let hadRemoveFromQuiz = Boolean(this.removeFromQuizFo)
        while (this.domElement.children.length > 0)
            this.domElement.removeChild(this.domElement.children[0]);

        if (Q3.editMode) { this.domElement.appendChild(this._path_editor); }
        this.domElement.appendChild(this._path);
        if (Q3.editMode) { this.domElement.appendChild(this._mouseOverVisualizer); }

        const { fnx, fny } = this._getFunctions(width, height);
        let path = this._createCurvedPathString(fnx, fny, true);

        /*if(this._roundRadius) {
            path = roundPathCorners(path, this._roundRadius, false);
        }*/

        if (path.indexOf("NaN") === -1 && !this._hasCustomPath) {
            this._path.setAttribute("d", path);
            this._path_editor.setAttribute("d", path);
        }

        if (this._editing) {
            this._points.forEach((p, i) => {
                const pointer = document.createElementNS(Base2DElement.Namespace, "circle");

                pointer.setAttribute("cx", fnx(p.x));
                pointer.setAttribute("cy", fny(p.y));
                pointer.setAttribute("r", VERTEX_SIZE / RenderManager.pixelRatio);
                pointer.setAttribute("index", i);

                this.domElement.appendChild(pointer);
            });
        }
        if (this.clonesFo) {
            this.clonesFo.setAttribute("x", fnx(Math.min(...this._points.map(p => p.x))))
            this.clonesFo.setAttribute("y", fny(Math.min(...this._points.map(p => p.y))))
            this.clonesFo.setAttribute("width", this.width*width*pr)
            this.clonesFo.setAttribute("height", this.height*height*pr);
        }

        if (hadRemoveFromQuiz) {
            this.createRemoveEl()
        }
        this._recalculateBounds();
    }
}

export default CustomizableRegion
export { CustomizableRegion };
